// src/components/Transfers.tsx — Inventory > Transfers (mobile-first step flow)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import {
  availableForItem,
  capQty,
  isKegPackage,
  type OnHandItem,
} from '../utils/transferItems';
import { matchKegToLine } from '../utils/kegLineMatch';
import KegScanPanel from './KegScanPanel';
import { centralToday, centralDaysAgo } from '../utils/centralDate';

interface TransferLine {
  id?: number;
  itemName: string;
  productId?: number | null;
  packageType?: string | null;
  qty: number;
  uom?: string | null;
  lineStatus?: string | null;
  kegCodes?: string[];
}

interface Transfer {
  id: number;
  transferNumber?: string | null;
  /** Alabama ABC Transfer Order number (plain integer, e.g. 295857). */
  toNumber?: string | null;
  transferDate: string;
  status: string;
  fromSiteId?: string | null;
  fromLocationId?: number | null;
  fromLocationName?: string | null;
  toSiteId?: string | null;
  toLocationId?: number | null;
  toLocationName?: string | null;
  notes?: string | null;
  transferredByEmail?: string | null;
  createdByEmail?: string | null;
  itemCount?: number;
  lines?: TransferLine[];
  toIsTaproom?: boolean;
  origin?: string | null;
  taproomOrderId?: number | null;
}

/** Madison ↔ Athens brewery coolers (TTB in-bond / 27 CFR 25.186). */
function isBreweryToBreweryTransfer(t: Transfer | null | undefined): boolean {
  const from = String(t?.fromSiteId || '');
  const to = String(t?.toSiteId || '');
  return from.startsWith('BR-') && to.startsWith('BR-') && from !== to;
}

interface LocationRow {
  locationId: number;
  siteId: string;
  name: string;
}

interface SiteRow {
  siteId: string;
  name: string;
}

type DatePreset = '' | '7d' | '30d' | '90d' | 'ytd' | 'custom';
type FlowStep = 1 | 2 | 3 | 4;

function todayIso() {
  return centralToday();
}

function daysAgoIso(n: number) {
  return centralDaysAgo(n);
}

function yearStartIso() {
  return `${centralToday().slice(0, 4)}-01-01`;
}

function siteLabel(sites: SiteRow[], siteId?: string | null) {
  if (!siteId) return '—';
  const hit = sites.find((s) => s.siteId === siteId);
  return hit?.name || siteId;
}

function isDraftStatus(status?: string | null) {
  return String(status || '').trim().toLowerCase() === 'draft';
}

const SHOW_TAPROOM_ORDERS_KEY = 'tilly.transfers.showTaproomOrders';

function readShowTaproomOrders(): boolean {
  try {
    return localStorage.getItem(SHOW_TAPROOM_ORDERS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeShowTaproomOrders(on: boolean): void {
  try {
    localStorage.setItem(SHOW_TAPROOM_ORDERS_KEY, on ? '1' : '0');
  } catch {
    /* private mode / blocked storage */
  }
}

function isTaproomOrderTransfer(transfer: { origin?: string | null; taproomOrderId?: number | null }) {
  return String(transfer.origin || '') === 'taproom-order' && transfer.taproomOrderId != null;
}

function transferLabel(transfer: { id: number; toNumber?: string | null; transferNumber?: string | null }) {
  if (transfer.toNumber) return `TO ${transfer.toNumber}`;
  if (transfer.transferNumber) return transfer.transferNumber;
  return `Draft #${transfer.id}`;
}

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );
  useEffect(() => {
    const apply = () => setIsNarrow(window.innerWidth <= 767);
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);
  return isNarrow;
}

function printPickList(t: Transfer, sites: SiteRow[]) {
  const w = window.open('', '_blank', 'noopener,noreferrer,width=800,height=900');
  if (!w) return;
  const lines = (t.lines || [])
    .map(
      (l) =>
        `<tr>
          <td>${escapeHtml(l.itemName)}</td>
          <td>${escapeHtml(t.fromLocationName || '')}</td>
          <td>${l.qty}</td>
          <td>${escapeHtml(l.uom || l.packageType || 'ea')}</td>
          <td>${escapeHtml((l.kegCodes || []).join(', '))}</td>
        </tr>`
    )
    .join('');
  w.document.write(`<!doctype html><html><head><title>Pick List ${t.toNumber || t.transferNumber || t.id}</title>
    <style>
      body{font-family:system-ui,sans-serif;padding:24px;color:#222}
      h1{font-size:20px;margin:0 0 8px}
      table{width:100%;border-collapse:collapse;margin-top:16px}
      th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:13px}
      th{background:#f3f3f3}
      .meta{color:#555;font-size:13px;line-height:1.5}
    </style></head><body>
    <h1>Transfer Pick List — ${escapeHtml(t.transferNumber || `Draft #${t.id}`)}</h1>
    <div class="meta">
      <div>TO #: ${escapeHtml(t.toNumber || '—')}</div>
      <div>Date: ${escapeHtml(t.transferDate)}</div>
      <div>Status: ${escapeHtml(t.status)}</div>
      <div>From: ${escapeHtml(siteLabel(sites, t.fromSiteId))} / ${escapeHtml(t.fromLocationName || '')}</div>
      <div>To: ${escapeHtml(siteLabel(sites, t.toSiteId))} / ${escapeHtml(t.toLocationName || '')}</div>
      <div>By: ${escapeHtml(t.transferredByEmail || t.createdByEmail || '')}</div>
      ${t.notes ? `<div>Memo: ${escapeHtml(t.notes)}</div>` : ''}
    </div>
    <table><thead><tr><th>Item</th><th>From Location</th><th>Qty</th><th>UOM</th><th>Assets</th></tr></thead>
    <tbody>${lines || '<tr><td colspan="5">No lines</td></tr>'}</tbody></table>
    <script>window.onload=()=>window.print()</script>
    </body></html>`);
  w.document.close();
}

function escapeHtml(s: string) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export const TransfersList: React.FC = () => {
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const [rows, setRows] = useState<Transfer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [fromSiteId, setFromSiteId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [transferredBy, setTransferredBy] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [q, setQ] = useState('');
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showTaproomOrders, setShowTaproomOrders] = useState(readShowTaproomOrders);

  useEffect(() => {
    if (datePreset === '7d') {
      setStartDate(daysAgoIso(7));
      setEndDate(todayIso());
    } else if (datePreset === '30d') {
      setStartDate(daysAgoIso(30));
      setEndDate(todayIso());
    } else if (datePreset === '90d') {
      setStartDate(daysAgoIso(90));
      setEndDate(todayIso());
    } else if (datePreset === 'ytd') {
      setStartDate(yearStartIso());
      setEndDate(todayIso());
    } else if (datePreset === '') {
      setStartDate('');
      setEndDate('');
    }
  }, [datePreset]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (fromSiteId) params.set('fromSiteId', fromSiteId);
    if (toLocationId) params.set('toLocationId', toLocationId);
    if (transferredBy.trim()) params.set('transferredBy', transferredBy.trim());
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (q.trim()) params.set('q', q.trim());
    if (showTaproomOrders) params.set('includeTaproom', '1');
    const res = await apiFetch(`${API_BASE_URL}/api/transfers?${params}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Failed to load transfers: HTTP ${res.status}`);
    setRows(await res.json());
  }, [status, fromSiteId, toLocationId, transferredBy, startDate, endDate, q, showTaproomOrders]);

  useEffect(() => {
    Promise.all([
      apiFetch(`${API_BASE_URL}/api/sites`, { headers: { Accept: 'application/json' } }),
      apiFetch(`${API_BASE_URL}/api/locations`, { headers: { Accept: 'application/json' } }),
    ])
      .then(async ([s, l]) => {
        if (s.ok) setSites(await s.json());
        if (l.ok) setLocations(await l.json());
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  const startTransfer = async () => {
    try {
      setError(null);
      const res = await apiFetch(`${API_BASE_URL}/api/transfers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ transferDate: todayIso() }),
      });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      navigate(`/transfers/${created.id}`);
    } catch (err: any) {
      setError('Failed to create transfer: ' + err.message);
    }
  };

  const deleteDraft = async (transfer: Transfer) => {
    if (!isDraftStatus(transfer.status)) return;
    const ok = window.confirm(
      `Delete draft ${transferLabel(transfer)}?\n\nScanned kegs on this draft will be released. Inventory and kegs are not changed. This cannot be undone.`
    );
    if (!ok) return;
    setDeletingId(transfer.id);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/transfers/${transfer.id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div
      className="page-container container flow-page"
      data-testid="transfers-list"
      data-narrow={isNarrow ? 'true' : 'false'}
    >
      <Link to="/inventory" className="flow-back">
        ← Inventory
      </Link>
      <h2 className="app-header">Transfers</h2>
      {error && <div className="error">{error}</div>}

      <div className="flow-toolbar">
        <button type="button" className="btn-new" onClick={startTransfer} data-testid="transfer-create">
          New Transfer
        </button>
        <button
          type="button"
          className={`btn btn-secondary transfer-taproom-toggle${showTaproomOrders ? ' is-on' : ''}`}
          aria-pressed={showTaproomOrders}
          data-testid="show-taproom-orders"
          onClick={() => {
            setShowTaproomOrders((prev) => {
              const next = !prev;
              writeShowTaproomOrders(next);
              return next;
            });
          }}
        >
          Show taproom orders
        </button>
        {isNarrow && (
          <button
            type="button"
            className="btn-secondary"
            style={{ minHeight: 44, background: '#EEE', color: '#333', width: '100%' }}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            {filtersOpen ? 'Hide filters' : 'Filters & search'}
          </button>
        )}
      </div>

      {(!isNarrow || filtersOpen) && (
        <div className="flow-filters">
          <label>
            Status
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="transfer-filter-status"
            >
              <option value="">All</option>
              <option value="complete">Complete</option>
              <option value="review">Review</option>
              <option value="draft">Draft</option>
            </select>
          </label>
          <label>
            From Site
            <select
              value={fromSiteId}
              onChange={(e) => setFromSiteId(e.target.value)}
              data-testid="transfer-filter-site"
            >
              <option value="">All</option>
              {sites.map((s) => (
                <option key={s.siteId} value={s.siteId}>
                  {s.name || s.siteId}
                </option>
              ))}
            </select>
          </label>
          <label>
            To Location
            <select
              value={toLocationId}
              onChange={(e) => setToLocationId(e.target.value)}
              data-testid="transfer-filter-to-location"
            >
              <option value="">All</option>
              {locations.map((l) => (
                <option key={l.locationId} value={l.locationId}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Transferred By
            <input
              type="text"
              value={transferredBy}
              onChange={(e) => setTransferredBy(e.target.value)}
              placeholder="email / name"
              data-testid="transfer-filter-by"
            />
          </label>
          <label>
            Transfer Date
            <select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value as DatePreset)}
              data-testid="transfer-filter-date-preset"
            >
              <option value="">Any</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="ytd">Year to date</option>
              <option value="custom">Custom</option>
            </select>
          </label>
          {datePreset === 'custom' && (
            <>
              <label>
                From
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </label>
              <label>
                To
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </label>
            </>
          )}
          <label>
            Search TO # / Ref
            <input
              type="search"
              placeholder="295857 or E-TR…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="transfer-filter-q"
            />
          </label>
        </div>
      )}

      <div className="flow-mobile-cards flow-card-list" data-testid="transfers-cards">
        {rows.map((r) => (
          <div key={r.id} className="flow-card" data-testid={`transfer-card-${r.id}`}>
            <button
              type="button"
              className="flow-card tappable"
              style={{ boxShadow: 'none', border: 'none', padding: 0, width: '100%' }}
              onClick={() => navigate(`/transfers/${r.id}`)}
            >
              <div className="flow-card-row">
                <p className="flow-card-title" style={{ margin: 0 }}>
                  {r.toNumber || r.transferNumber || `Draft #${r.id}`}
                </p>
                <span className={`flow-badge ${r.status}`}>{r.status}</span>
              </div>
              <p className="flow-card-meta">
                {siteLabel(sites, r.fromSiteId)} → {r.toLocationName || '—'}
              </p>
              <p className="flow-card-meta">
                {r.transferDate} · {r.itemCount ?? 0} items ·{' '}
                {r.transferredByEmail || r.createdByEmail || '—'}
              </p>
            </button>
            {isTaproomOrderTransfer(r) && (
              <Link
                to={`/taproom-orders/${r.taproomOrderId}`}
                className="flow-badge taproom-order"
                data-testid={`transfer-taproom-link-${r.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                Taproom order
              </Link>
            )}
            {isDraftStatus(r.status) && (
              <button
                type="button"
                className="btn-danger"
                data-testid={`transfer-delete-${r.id}`}
                style={{ minHeight: 44, width: '100%', marginTop: 10 }}
                disabled={deletingId === r.id}
                onClick={() => void deleteDraft(r)}
              >
                {deletingId === r.id ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        ))}
        {!rows.length && <p className="flow-hint">No transfers yet.</p>}
      </div>

      <table className="inventory-table flow-desktop-table" data-testid="transfers-table">
        <thead>
          <tr>
            <th>TO #</th>
            <th>Transfer</th>
            <th>Status</th>
            <th>From Site</th>
            <th>To Location</th>
            <th>Transfer Date</th>
            <th>Transferred By</th>
            <th>Items</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`transfer-row-${r.id}`}>
              <td data-testid={`transfer-to-${r.id}`}>{r.toNumber || '—'}</td>
              <td>
                {r.transferNumber || `Draft #${r.id}`}
                {isTaproomOrderTransfer(r) && (
                  <>
                    {' '}
                    <Link
                      to={`/taproom-orders/${r.taproomOrderId}`}
                      className="flow-badge taproom-order"
                      data-testid={`transfer-table-taproom-link-${r.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      Taproom order
                    </Link>
                  </>
                )}
              </td>
              <td style={{ textTransform: 'capitalize' }}>{r.status}</td>
              <td>{siteLabel(sites, r.fromSiteId)}</td>
              <td>{r.toLocationName || '—'}</td>
              <td>{r.transferDate}</td>
              <td>{r.transferredByEmail || r.createdByEmail || '—'}</td>
              <td>{r.itemCount ?? 0}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <button type="button" onClick={() => navigate(`/transfers/${r.id}`)}>
                  View
                </button>
                {isDraftStatus(r.status) && (
                  <button
                    type="button"
                    className="btn-danger"
                    data-testid={`transfer-table-delete-${r.id}`}
                    style={{ marginLeft: 8, minHeight: 36 }}
                    disabled={deletingId === r.id}
                    onClick={() => void deleteDraft(r)}
                  >
                    {deletingId === r.id ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={9} style={{ color: '#777' }}>
                No transfers yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const TransferDetail: React.FC = () => {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [onHandItems, setOnHandItems] = useState<OnHandItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [step, setStep] = useState<FlowStep>(1);
  const [scanLineIndex, setScanLineIndex] = useState<number | null>(null);
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [editing, setEditing] = useState(false);
  const [itemQuery, setItemQuery] = useState('');

  const editable =
    !!transfer && transfer.status !== 'complete' && (editing || transfer.status === 'draft');

  const load = useCallback(async () => {
    const res = await apiFetch(`${API_BASE_URL}/api/transfers/${transferId}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    setTransfer(data);
    setLines(
      (data.lines || []).map((l: TransferLine) => ({
        ...l,
        kegCodes: l.kegCodes || [],
      }))
    );
    setEditing(data.status === 'draft');
  }, [transferId]);

  useEffect(() => {
    load().catch((err: Error) => setError(err.message));
  }, [load]);

  useEffect(() => {
    Promise.all([
      apiFetch(`${API_BASE_URL}/api/locations`, { headers: { Accept: 'application/json' } }),
      apiFetch(`${API_BASE_URL}/api/sites`, { headers: { Accept: 'application/json' } }),
    ])
      .then(async ([locRes, siteRes]) => {
        if (locRes.ok) setLocations(await locRes.json());
        if (siteRes.ok) setSites(await siteRes.json());
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const locId = transfer?.fromLocationId;
    if (!locId) {
      setOnHandItems([]);
      return;
    }
    let cancelled = false;
    apiFetch(`${API_BASE_URL}/api/locations/${locId}/on-hand`, {
      headers: { Accept: 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const items = (data.items || []).filter(
          (i: OnHandItem) => Number(i.quantity) > 0
        ) as OnHandItem[];
        setOnHandItems(items);
      })
      .catch(() => {
        if (!cancelled) setOnHandItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [transfer?.fromLocationId]);

  const filteredOnHand = useMemo(() => {
    const q = itemQuery.trim().toLowerCase();
    if (!q) return onHandItems;
    return onHandItems.filter((i) => i.itemName.toLowerCase().includes(q));
  }, [onHandItems, itemQuery]);

  const kegLines = useMemo(() => lines.filter(isKegPackage), [lines]);
  const kegScanIncomplete = useMemo(
    () =>
      kegLines.some((l) => (l.kegCodes || []).length !== Math.round(Number(l.qty) || 0)),
    [kegLines]
  );
  const totalKegNeed = useMemo(
    () => kegLines.reduce((s, l) => s + Math.round(Number(l.qty) || 0), 0),
    [kegLines]
  );
  const totalKegHave = useMemo(
    () => kegLines.reduce((s, l) => s + (l.kegCodes || []).length, 0),
    [kegLines]
  );

  const locsForSite = useMemo(() => {
    const bySite = (siteId?: string | null) =>
      locations.filter((l) => !siteId || l.siteId === siteId);
    return {
      from: bySite(transfer?.fromSiteId),
      to: bySite(transfer?.toSiteId),
    };
  }, [locations, transfer?.fromSiteId, transfer?.toSiteId]);

  const step1Ok = !!(transfer?.fromLocationId && transfer?.toLocationId && transfer?.transferDate);
  const step2Ok = lines.some((l) => l.itemName && Number(l.qty) > 0);
  const needsScan = kegLines.length > 0;
  const step3Ok = !needsScan || !kegScanIncomplete;

  const patchHeader = async (patch: Partial<Transfer>) => {
    if (!transfer) return;
    if (transfer.status === 'complete') {
      const keys = Object.keys(patch);
      if (!(keys.length === 1 && keys[0] === 'toNumber')) return;
    }
    setSaving(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/transfers/${transfer.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(await res.text());
      setTransfer(await res.json());
      if ('fromLocationId' in patch || 'fromSiteId' in patch) {
        setLines((prev) => prev.map((l) => ({ ...l, kegCodes: [] })));
        setScanLineIndex(null);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveLines = async (nextLines: TransferLine[]) => {
    if (!transfer || transfer.status === 'complete') return;
    setSaving(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/transfers/${transfer.id}/lines`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ lines: nextLines }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setTransfer(data);
      setLines(
        (data.lines || []).map((l: TransferLine) => ({
          ...l,
          kegCodes: l.kegCodes || [],
        }))
      );
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const complete = async () => {
    if (!transfer) return;
    try {
      setError(null);
      if (kegScanIncomplete) {
        setError('Scan assets: each keg line needs scanned serials matching qty');
        setStep(3);
        return;
      }
      if (transfer.status !== 'complete') await saveLines(lines);
      const res = await apiFetch(`${API_BASE_URL}/api/transfers/${transfer.id}/complete`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(await res.text());
      setTransfer(await res.json());
      setEditing(false);
      setStep(4);
    } catch (err: any) {
      setError('Complete failed: ' + err.message);
    }
  };

  const tryAgain = async () => {
    if (!transfer) return;
    if (transfer.status === 'complete') {
      try {
        setError(null);
        const res = await apiFetch(`${API_BASE_URL}/api/transfers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            transferDate: todayIso(),
            fromSiteId: transfer.fromSiteId,
            fromLocationId: transfer.fromLocationId,
            toSiteId: transfer.toSiteId,
            toLocationId: transfer.toLocationId,
            notes: transfer.notes
              ? `Retry of ${transfer.transferNumber || transfer.id}: ${transfer.notes}`
              : `Retry of ${transfer.transferNumber || transfer.id}`,
          }),
        });
        if (!res.ok) throw new Error(await res.text());
        const created = await res.json();
        if (transfer.lines?.length) {
          await apiFetch(`${API_BASE_URL}/api/transfers/${created.id}/lines`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              lines: transfer.lines.map((l) => ({
                itemName: l.itemName,
                qty: l.qty,
                packageType: l.packageType,
                uom: l.uom,
                kegCodes: [],
              })),
            }),
          });
        }
        navigate(`/transfers/${created.id}`);
      } catch (err: any) {
        setError('Try again failed: ' + err.message);
      }
      return;
    }
    await complete();
  };

  const remove = async () => {
    if (!transfer || !isDraftStatus(transfer.status)) return;
    const ok = window.confirm(
      `Delete draft ${transferLabel(transfer)}?\n\nScanned kegs on this draft will be released. Inventory and kegs are not changed. This cannot be undone.`
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/transfers/${transfer.id}`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      navigate('/transfers');
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    } finally {
      setDeleting(false);
    }
  };

  const addKegToLine = async (codeRaw: string) => {
    if (scanLineIndex == null || !transfer) {
      throw new Error('No scan line selected');
    }
    const code = normalizeKegCode(codeRaw);
    if (!isValidKegCode(code)) {
      throw new Error('Invalid keg code');
    }
    const line = lines[scanLineIndex];
    if (!line) throw new Error('Line not found');
    const need = Math.round(Number(line.qty) || 0);
    const existing = (line.kegCodes || []).map((c) => normalizeKegCode(c));
    if (existing.includes(code)) {
      throw new Error(`Keg ${code} already added`);
    }
    if (existing.length >= need) {
      throw new Error(`Already scanned ${need} keg(s) for this line`);
    }

    const res = await apiFetch(`${API_BASE_URL}/api/kegs/${encodeURIComponent(code)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      throw new Error(`Keg ${code} not found`);
    }
    const keg = await res.json();
    if (String(keg.status || '').toLowerCase() !== 'filled') {
      throw new Error(`Keg ${code} must be Filled (status=${keg.status})`);
    }
    if (
      transfer.fromLocationId != null &&
      Number(keg.locationId) !== Number(transfer.fromLocationId)
    ) {
      throw new Error(`Keg ${code} is not at the FROM location`);
    }
    const match = matchKegToLine(
      {
        code,
        productId: keg.productId,
        productName: keg.productName,
        packagingType: keg.packagingType,
        packageType: keg.packageType,
      },
      {
        itemName: line.itemName,
        productId: line.productId,
        packageType: line.packageType,
      },
      { kegCode: code }
    );
    if (!match.ok) {
      throw new Error(match.message);
    }

    setError(null);
    setLines((prev) => {
      const next = [...prev];
      const cur = { ...next[scanLineIndex], kegCodes: [...(next[scanLineIndex].kegCodes || [])] };
      if (!cur.kegCodes!.includes(code)) cur.kegCodes!.push(code);
      next[scanLineIndex] = cur;
      return next;
    });
  };

  const setLineQty = (itemName: string, qty: number, unit?: string) => {
    const max = availableForItem(onHandItems, itemName);
    const capped = capQty(qty, max);
    setLines((prev) => {
      const idx = prev.findIndex((l) => l.itemName === itemName);
      if (capped <= 0) {
        return prev.filter((l) => l.itemName !== itemName);
      }
      if (idx >= 0) {
        const next = [...prev];
        const keep = Math.round(capped);
        next[idx] = {
          ...next[idx],
          qty: capped,
          uom: unit || next[idx].uom || 'ea',
          kegCodes: (next[idx].kegCodes || []).slice(0, keep),
        };
        return next;
      }
      return [
        ...prev,
        { itemName, qty: capped, uom: unit || 'ea', kegCodes: [] },
      ];
    });
  };

  const goNext = async () => {
    setError(null);
    if (step === 1) {
      if (!step1Ok) {
        setError('Set FROM location, TO location, and date');
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      if (!step2Ok) {
        setError('Add at least one item with quantity');
        return;
      }
      try {
        await saveLines(lines.filter((l) => l.itemName && Number(l.qty) > 0));
      } catch {
        return;
      }
      setStep(needsScan ? 3 : 4);
      return;
    }
    if (step === 3) {
      if (!step3Ok) {
        setError('Scan all required keg serials before continuing');
        return;
      }
      try {
        await saveLines(lines);
      } catch {
        return;
      }
      setStep(4);
    }
  };

  const goBack = () => {
    if (step === 1) {
      navigate('/transfers');
      return;
    }
    if (step === 4 && needsScan) setStep(3);
    else if (step === 4) setStep(2);
    else if (step === 3) setStep(2);
    else setStep(1);
  };

  if (!transfer) {
    return (
      <div className="page-container container flow-page">
        <p>{error || 'Loading…'}</p>
      </div>
    );
  }

  const selectedMap = new Map(lines.map((l) => [l.itemName, l]));

  return (
    <div
      className="page-container container flow-page"
      data-testid="transfer-detail"
      data-narrow={isNarrow ? 'true' : 'false'}
    >
      <Link to="/transfers" className="flow-back">
        ← Transfers
      </Link>
      <h2 className="app-header">
        {transfer.transferNumber || `Draft #${transfer.id}`}
        {transfer.toNumber ? ` · TO ${transfer.toNumber}` : ''}
      </h2>
      {isTaproomOrderTransfer(transfer) && (
        <p>
          <Link
            to={`/taproom-orders/${transfer.taproomOrderId}`}
            className="flow-badge taproom-order"
            data-testid="transfer-detail-taproom-link"
          >
            Taproom order
          </Link>
        </p>
      )}
      {transfer.toIsTaproom && (
        <p className="flow-hint">Brewery → taproom (outbound / ABC)</p>
      )}
      {error && <div className="error">{error}</div>}

      {transfer.status !== 'complete' && (
        <div className="flow-steps" role="tablist" aria-label="Transfer steps">
          {(
            [
              { n: 1 as FlowStep, label: '1 From/To' },
              { n: 2 as FlowStep, label: '2 Items' },
              ...(needsScan || step === 3
                ? [{ n: 3 as FlowStep, label: '3 Scan' }]
                : []),
              { n: 4 as FlowStep, label: needsScan ? '4 Review' : '3 Review' },
            ] as { n: FlowStep; label: string }[]
          ).map(({ n, label }) => (
            <button
              key={n}
              type="button"
              className={`flow-step ${step === n ? 'active' : ''} ${
                (n === 1 && step1Ok) ||
                (n === 2 && step2Ok) ||
                (n === 3 && step3Ok) ||
                (n < step)
                  ? 'done'
                  : ''
              }`}
              onClick={() => setStep(n)}
              data-testid={`transfer-step-${n}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {transfer.status === 'complete' && (
        <div className="flow-toolbar" style={{ marginBottom: 12 }}>
          <button type="button" onClick={() => printPickList(transfer, sites)}>
            Print Pick List
          </button>
          {isBreweryToBreweryTransfer(transfer) && (
            <button
              type="button"
              onClick={() => navigate(`/transfers/${transfer.id}/in-bond-invoice`)}
              data-testid="transfer-print-inbond"
            >
              Print Transfer Invoice
            </button>
          )}
          <button type="button" onClick={tryAgain} data-testid="transfer-try-again">
            Try Transfer Again
          </button>
        </div>
      )}

      {/* Step 1 — From/To + date */}
      {(step === 1 || transfer.status === 'complete') && (
        <div data-testid="transfer-step-locations">
          {transfer.status !== 'complete' && (
            <h3 className="flow-section-title">From / To &amp; date</h3>
          )}
          <div className="flow-field-grid">
            <div className="flow-field">
              <label htmlFor="xfer-to-number">TO # (ABC)</label>
              <input
                id="xfer-to-number"
                value={transfer.toNumber || ''}
                placeholder="295857"
                data-testid="transfer-to-number"
                onChange={(e) => setTransfer({ ...transfer, toNumber: e.target.value })}
                onBlur={() =>
                  patchHeader({
                    toNumber: transfer.toNumber ? transfer.toNumber.trim() : null,
                  })
                }
              />
            </div>
            <div className="flow-field">
              <label htmlFor="xfer-date">Date</label>
              <input
                id="xfer-date"
                type="date"
                value={transfer.transferDate || ''}
                disabled={!editable}
                onChange={(e) => patchHeader({ transferDate: e.target.value })}
              />
            </div>
            <div className="flow-field">
              <label htmlFor="xfer-from-site">From Site</label>
              <select
                id="xfer-from-site"
                value={transfer.fromSiteId || ''}
                disabled={!editable}
                onChange={(e) =>
                  patchHeader({ fromSiteId: e.target.value, fromLocationId: null })
                }
              >
                <option value="">—</option>
                {sites.map((s) => (
                  <option key={s.siteId} value={s.siteId}>
                    {s.name || s.siteId}
                  </option>
                ))}
              </select>
            </div>
            <div className="flow-field">
              <label htmlFor="xfer-from-loc">From Location</label>
              <select
                id="xfer-from-loc"
                value={transfer.fromLocationId ?? ''}
                disabled={!editable}
                onChange={(e) =>
                  patchHeader({
                    fromLocationId: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value="">—</option>
                {(locsForSite.from.length ? locsForSite.from : locations).map((l) => (
                  <option key={l.locationId} value={l.locationId}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flow-field">
              <label htmlFor="xfer-to-site">To Site</label>
              <select
                id="xfer-to-site"
                value={transfer.toSiteId || ''}
                disabled={!editable}
                onChange={(e) =>
                  patchHeader({ toSiteId: e.target.value, toLocationId: null })
                }
              >
                <option value="">—</option>
                {sites.map((s) => (
                  <option key={s.siteId} value={s.siteId}>
                    {s.name || s.siteId}
                  </option>
                ))}
              </select>
            </div>
            <div className="flow-field">
              <label htmlFor="xfer-to-loc">To Location</label>
              <select
                id="xfer-to-loc"
                value={transfer.toLocationId ?? ''}
                disabled={!editable}
                onChange={(e) =>
                  patchHeader({
                    toLocationId: e.target.value ? Number(e.target.value) : null,
                  })
                }
              >
                <option value="">—</option>
                {(locsForSite.to.length ? locsForSite.to : locations).map((l) => (
                  <option key={l.locationId} value={l.locationId}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flow-field" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="xfer-memo">Memo</label>
              <textarea
                id="xfer-memo"
                value={transfer.notes || ''}
                disabled={!editable}
                onChange={(e) => setTransfer({ ...transfer, notes: e.target.value })}
                onBlur={() => patchHeader({ notes: transfer.notes })}
                rows={2}
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 2 — Items as on-hand cards + steppers */}
      {step === 2 && transfer.status !== 'complete' && (
        <div data-testid="transfer-step-items">
          <h3 className="flow-section-title">Add items</h3>
          {!transfer.fromLocationId ? (
            <p className="flow-hint" data-testid="transfer-need-from-location">
              Set a FROM location in step 1 to see on-hand items.
            </p>
          ) : (
            <>
              <div className="flow-field">
                <label htmlFor="xfer-item-search">Search on-hand</label>
                <input
                  id="xfer-item-search"
                  value={itemQuery}
                  onChange={(e) => setItemQuery(e.target.value)}
                  placeholder="Filter items…"
                  data-testid="transfer-item-search"
                  autoComplete="off"
                />
              </div>

              {lines.length > 0 && (
                <>
                  <p className="flow-hint">Selected ({lines.length})</p>
                  <div className="flow-card-list" style={{ marginBottom: 16 }}>
                    {lines.map((line) => {
                      const avail = availableForItem(onHandItems, line.itemName);
                      const unit =
                        onHandItems.find((i) => i.itemName === line.itemName)?.unit ||
                        line.uom ||
                        'ea';
                      return (
                        <div key={line.itemName} className="flow-card selected">
                          <p className="flow-card-title">{line.itemName}</p>
                          <p className="flow-card-meta">
                            Available {avail} {unit}
                          </p>
                          <div className="flow-card-row">
                            <div className="flow-stepper" data-testid={`transfer-stepper-${line.itemName}`}>
                              <button
                                type="button"
                                aria-label="Decrease qty"
                                onClick={() =>
                                  setLineQty(line.itemName, Number(line.qty) - 1, unit)
                                }
                              >
                                −
                              </button>
                              <span className="flow-stepper-value">{line.qty}</span>
                              <button
                                type="button"
                                aria-label="Increase qty"
                                onClick={() =>
                                  setLineQty(line.itemName, Number(line.qty) + 1, unit)
                                }
                              >
                                +
                              </button>
                            </div>
                            <button
                              type="button"
                              style={{ minHeight: 44, background: '#ffe6e6', color: '#a00' }}
                              onClick={() => setLineQty(line.itemName, 0)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              <p className="flow-hint">On-hand at {transfer.fromLocationName}</p>
              <div className="flow-card-list" data-testid="transfer-onhand-cards">
                {filteredOnHand.map((item) => {
                  const selected = selectedMap.get(item.itemName);
                  return (
                    <button
                      key={item.itemName}
                      type="button"
                      className={`flow-card tappable ${selected ? 'selected' : ''}`}
                      data-testid={`transfer-onhand-${item.itemName}`}
                      onClick={() => {
                        if (selected) return;
                        setLineQty(item.itemName, 1, item.unit);
                      }}
                    >
                      <p className="flow-card-title">{item.itemName}</p>
                      <div className="flow-card-row">
                        <span className="flow-card-meta">
                          {item.quantity} {item.unit || 'ea'} available
                        </span>
                        {selected ? (
                          <span className="flow-badge complete">Added · {selected.qty}</span>
                        ) : (
                          <span className="flow-badge">Tap to add</span>
                        )}
                      </div>
                    </button>
                  );
                })}
                {!filteredOnHand.length && (
                  <p className="flow-hint">No items with on-hand &gt; 0</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3 — Scan kegs */}
      {step === 3 && transfer.status !== 'complete' && (
        <div data-testid="transfer-assets-step">
          <h3 className="flow-section-title">Scan assets</h3>
          <p
            className={`flow-scan-count ${totalKegHave === totalKegNeed ? 'complete' : ''}`}
            data-testid="transfer-keg-total-progress"
          >
            {totalKegHave} of {totalKegNeed} scanned
          </p>
          <p className="flow-hint">
            Each keg must exist, be Filled with that product, and be at the FROM location.
          </p>
          <div className="flow-card-list">
            {lines.map((line, idx) => {
              if (!isKegPackage(line)) return null;
              const need = Math.round(Number(line.qty) || 0);
              const have = (line.kegCodes || []).length;
              return (
                <div key={idx} className="flow-card">
                  <p className="flow-card-title">{line.itemName}</p>
                  <p
                    className={`flow-scan-count ${have === need ? 'complete' : ''}`}
                    style={{ fontSize: 18, textAlign: 'left' }}
                    data-testid={`transfer-keg-progress-${idx}`}
                  >
                    {have} of {need} scanned
                  </p>
                  <p className="flow-card-meta">
                    {(line.kegCodes || []).join(', ') || 'No serials yet'}
                  </p>
                  {editable && (
                    <button
                      type="button"
                      style={{ minHeight: 44, width: '100%', marginTop: 8 }}
                      onClick={() => setScanLineIndex(idx)}
                      data-testid={`transfer-scan-btn-${idx}`}
                    >
                      {have === need ? 'Edit scans' : 'Scan kegs'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {scanLineIndex != null && editable && lines[scanLineIndex] && (
            <KegScanPanel
              title={`Scan kegs for ${lines[scanLineIndex].itemName || 'line'}`}
              codes={lines[scanLineIndex].kegCodes || []}
              requiredCount={Math.round(Number(lines[scanLineIndex].qty) || 0)}
              onAdd={addKegToLine}
              onRemove={(code) => {
                const drop = normalizeKegCode(code);
                setLines((prev) => {
                  const next = [...prev];
                  const cur = {
                    ...next[scanLineIndex],
                    kegCodes: (next[scanLineIndex].kegCodes || []).filter(
                      (c) => normalizeKegCode(c) !== drop
                    ),
                  };
                  next[scanLineIndex] = cur;
                  return next;
                });
              }}
              onDone={() => setScanLineIndex(null)}
              largeInput
            />
          )}
        </div>
      )}

      {/* Step 4 — Review */}
      {step === 4 && (
        <div data-testid="transfer-step-review">
          <h3 className="flow-section-title">Review &amp; complete</h3>
          <div className="flow-card" style={{ marginBottom: 12 }}>
            <p className="flow-card-meta">
              <strong>TO #:</strong> {transfer.toNumber || '—'}
            </p>
            <p className="flow-card-meta">
              <strong>Date:</strong> {transfer.transferDate}
            </p>
            <p className="flow-card-meta">
              <strong>From:</strong> {siteLabel(sites, transfer.fromSiteId)} /{' '}
              {transfer.fromLocationName || '—'}
            </p>
            <p className="flow-card-meta">
              <strong>To:</strong> {siteLabel(sites, transfer.toSiteId)} /{' '}
              {transfer.toLocationName || '—'}
            </p>
            <p className="flow-card-meta">
              <strong>Status:</strong> {transfer.status}
            </p>
          </div>
          <div className="flow-card-list">
            {lines.map((line, idx) => (
              <div key={idx} className="flow-card">
                <p className="flow-card-title">{line.itemName}</p>
                <p className="flow-card-meta">
                  Qty {line.qty} {line.uom || line.packageType || 'ea'}
                </p>
                {isKegPackage(line) && (
                  <p className="flow-card-meta">
                    Kegs: {(line.kegCodes || []).join(', ') || '—'} (
                    {(line.kegCodes || []).length} of {Math.round(Number(line.qty) || 0)})
                  </p>
                )}
              </div>
            ))}
            {!lines.length && <p className="flow-hint">No lines</p>}
          </div>
          {transfer.status !== 'complete' && (
            <div className="flow-toolbar" style={{ marginTop: 12 }}>
              <button type="button" onClick={() => printPickList(transfer, sites)}>
                Print Pick List
              </button>
              {isBreweryToBreweryTransfer(transfer) && (
                <button
                  type="button"
                  onClick={() => navigate(`/transfers/${transfer.id}/in-bond-invoice`)}
                  data-testid="transfer-print-inbond"
                >
                  Print Transfer Invoice
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {transfer.status !== 'complete' && (
        <div className="flow-sticky-bar">
          <button type="button" className="btn-secondary" onClick={goBack} disabled={deleting}>
            {step === 1 ? 'Back' : 'Previous'}
          </button>
          {isDraftStatus(transfer.status) && (
            <button
              type="button"
              className="btn-danger"
              data-testid="transfer-delete-draft"
              onClick={() => void remove()}
              disabled={saving || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          {step < 4 ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => void goNext()}
              disabled={saving || deleting}
              data-testid="transfer-next"
            >
              {saving ? 'Saving…' : 'Next'}
            </button>
          ) : (
            <button
              type="button"
              className="btn-complete"
              data-testid="transfer-complete"
              onClick={() => void complete()}
              disabled={saving || deleting}
            >
              Complete transfer
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TransfersList;
